import type { Exercise } from '../types';

/**
 * Bundled exercise pool — used by tests, the marketing playground, the plan
 * editor's combobox, and as a fallback when the seeded `exercises` table is
 * empty. Real production pool comes from `supabase/seed/exercises.seed.ts`
 * pulling wger.de (~800 exercises).
 *
 * Names are baked in PL/DE here so the playground and grids work without any
 * backend. The seed script can use these as canonical translations when it
 * conflicts with the wger-translated value.
 */
export const FIXTURE_EXERCISES: Exercise[] = [
  // ===========================================================================
  // SQUAT pattern
  // ===========================================================================
  {
    id: 'back_squat',
    name: 'Back Squat',
    name_pl: 'Przysiad ze sztangą',
    name_de: 'Kniebeuge mit Langhantel',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes', 'core'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury', 'spinal_flexion_loaded'],
    movement_pattern: 'squat',
  },
  {
    id: 'front_squat',
    name: 'Front Squat',
    name_pl: 'Przysiad przedni',
    name_de: 'Frontkniebeuge',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['core', 'glutes'],
    equipment_required: ['barbell'],
    difficulty: 'advanced',
    contraindications: ['wrist_issue'],
    movement_pattern: 'squat',
  },
  {
    id: 'goblet_squat',
    name: 'Goblet Squat',
    name_pl: 'Przysiad z hantlą trzymaną pod brodą',
    name_de: 'Goblet-Kniebeuge',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes', 'core'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['knee_issue', 'deep_knee_flexion'],
    movement_pattern: 'squat',
  },
  {
    id: 'bodyweight_squat',
    name: 'Bodyweight Squat',
    name_pl: 'Przysiad bez obciążenia',
    name_de: 'Kniebeuge ohne Gewicht',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes'],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'squat',
  },
  {
    id: 'leg_press',
    name: 'Leg Press',
    name_pl: 'Wyciskanie na suwnicy',
    name_de: 'Beinpresse',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes'],
    equipment_required: ['machines'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'squat',
  },
  {
    id: 'bulgarian_split_squat',
    name: 'Bulgarian Split Squat',
    name_pl: 'Przysiad bułgarski',
    name_de: 'Bulgarian Split Squat',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes', 'hamstrings'],
    equipment_required: ['dumbbells'],
    difficulty: 'intermediate',
    contraindications: ['knee_issue'],
    movement_pattern: 'squat',
  },
  {
    id: 'walking_lunge',
    name: 'Walking Lunge',
    name_pl: 'Wykrok chodzony',
    name_de: 'Ausfallschritte im Gehen',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes', 'hamstrings'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['knee_issue'],
    movement_pattern: 'squat',
  },
  {
    id: 'step_up',
    name: 'Step-up',
    name_pl: 'Wejście na podest',
    name_de: 'Step-up',
    primary_muscle: 'quadriceps',
    secondary_muscles: ['glutes'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['knee_issue'],
    movement_pattern: 'squat',
  },
  {
    id: 'leg_extension',
    name: 'Leg Extension',
    name_pl: 'Prostowanie nóg na maszynie',
    name_de: 'Beinstrecker',
    primary_muscle: 'quadriceps',
    secondary_muscles: [],
    equipment_required: ['machines'],
    difficulty: 'beginner',
    contraindications: ['knee_issue'],
    movement_pattern: 'squat',
  },

  // ===========================================================================
  // HINGE pattern
  // ===========================================================================
  {
    id: 'deadlift',
    name: 'Deadlift',
    name_pl: 'Martwy ciąg',
    name_de: 'Kreuzheben',
    primary_muscle: 'back',
    secondary_muscles: ['hamstrings', 'glutes', 'core'],
    equipment_required: ['barbell'],
    difficulty: 'advanced',
    contraindications: ['lower_back_injury', 'spinal_flexion_loaded'],
    movement_pattern: 'hinge',
  },
  {
    id: 'rdl_bb',
    name: 'Barbell Romanian Deadlift',
    name_pl: 'Martwy ciąg rumuński ze sztangą',
    name_de: 'Rumänisches Kreuzheben mit Langhantel',
    primary_muscle: 'hamstrings',
    secondary_muscles: ['glutes', 'back'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'hinge',
  },
  {
    id: 'rdl_db',
    name: 'Dumbbell Romanian Deadlift',
    name_pl: 'Martwy ciąg rumuński z hantlami',
    name_de: 'Kurzhantel-Rumänisches Kreuzheben',
    primary_muscle: 'hamstrings',
    secondary_muscles: ['glutes', 'back'],
    equipment_required: ['dumbbells'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'hinge',
  },
  {
    id: 'good_morning',
    name: 'Good Morning',
    name_pl: 'Good morning',
    name_de: 'Good Morning',
    primary_muscle: 'hamstrings',
    secondary_muscles: ['glutes', 'back'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury', 'spinal_flexion_loaded'],
    movement_pattern: 'hinge',
  },
  {
    id: 'kb_swing',
    name: 'Kettlebell Swing',
    name_pl: 'Wymachy kettlebell',
    name_de: 'Kettlebell-Schwung',
    primary_muscle: 'glutes',
    secondary_muscles: ['hamstrings', 'back', 'core'],
    equipment_required: ['dumbbells'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'hinge',
  },
  {
    id: 'hip_thrust',
    name: 'Hip Thrust',
    name_pl: 'Hip thrust',
    name_de: 'Hip Thrust',
    primary_muscle: 'glutes',
    secondary_muscles: ['hamstrings'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: [],
    movement_pattern: 'hinge',
  },
  {
    id: 'glute_bridge',
    name: 'Glute Bridge',
    name_pl: 'Most biodrowy',
    name_de: 'Beckenheben',
    primary_muscle: 'glutes',
    secondary_muscles: ['hamstrings'],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'hinge',
  },
  {
    id: 'leg_curl',
    name: 'Lying Leg Curl',
    name_pl: 'Uginanie nóg leżąc',
    name_de: 'Beinbeuger liegend',
    primary_muscle: 'hamstrings',
    secondary_muscles: [],
    equipment_required: ['machines'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'hinge',
  },

  // ===========================================================================
  // PUSH pattern — chest
  // ===========================================================================
  {
    id: 'bench_press',
    name: 'Bench Press',
    name_pl: 'Wyciskanie sztangi na ławce',
    name_de: 'Bankdrücken',
    primary_muscle: 'chest',
    secondary_muscles: ['triceps', 'shoulders'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },
  {
    id: 'incline_bench_press',
    name: 'Incline Bench Press',
    name_pl: 'Wyciskanie sztangi na ławce skośnej',
    name_de: 'Schrägbankdrücken',
    primary_muscle: 'chest',
    secondary_muscles: ['shoulders', 'triceps'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },
  {
    id: 'db_bench',
    name: 'Dumbbell Bench Press',
    name_pl: 'Wyciskanie hantli na ławce',
    name_de: 'Kurzhantel-Bankdrücken',
    primary_muscle: 'chest',
    secondary_muscles: ['triceps', 'shoulders'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },
  {
    id: 'incline_db_press',
    name: 'Incline Dumbbell Press',
    name_pl: 'Wyciskanie hantli na ławce skośnej',
    name_de: 'Schräges Kurzhantel-Bankdrücken',
    primary_muscle: 'chest',
    secondary_muscles: ['shoulders', 'triceps'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },
  {
    id: 'pushup',
    name: 'Push-up',
    name_pl: 'Pompka',
    name_de: 'Liegestütz',
    primary_muscle: 'chest',
    secondary_muscles: ['triceps', 'shoulders', 'core'],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: ['wrist_issue'],
    movement_pattern: 'push',
  },
  {
    id: 'dip',
    name: 'Dip',
    name_pl: 'Dipy na poręczach',
    name_de: 'Dips',
    primary_muscle: 'chest',
    secondary_muscles: ['triceps', 'shoulders'],
    equipment_required: ['bodyweight'],
    difficulty: 'advanced',
    contraindications: ['shoulder_impingement', 'elbow_issue'],
    movement_pattern: 'push',
  },
  {
    id: 'cable_fly',
    name: 'Cable Fly',
    name_pl: 'Rozpiętki na bramie',
    name_de: 'Kabelfliegen',
    primary_muscle: 'chest',
    secondary_muscles: ['shoulders'],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },

  // ===========================================================================
  // PUSH pattern — shoulders
  // ===========================================================================
  {
    id: 'overhead_press',
    name: 'Overhead Press',
    name_pl: 'Wyciskanie żołnierskie',
    name_de: 'Schulterdrücken',
    primary_muscle: 'shoulders',
    secondary_muscles: ['triceps', 'core'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['shoulder_impingement', 'overhead_pressing'],
    movement_pattern: 'push',
  },
  {
    id: 'db_shoulder_press',
    name: 'Dumbbell Shoulder Press',
    name_pl: 'Wyciskanie hantli nad głowę',
    name_de: 'Kurzhantel-Schulterdrücken',
    primary_muscle: 'shoulders',
    secondary_muscles: ['triceps'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['overhead_pressing'],
    movement_pattern: 'push',
  },
  {
    id: 'lateral_raise',
    name: 'Lateral Raise',
    name_pl: 'Unoszenie hantli bokiem',
    name_de: 'Seitheben',
    primary_muscle: 'shoulders',
    secondary_muscles: [],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },
  {
    id: 'cable_lateral_raise',
    name: 'Cable Lateral Raise',
    name_pl: 'Unoszenie ramienia bokiem na wyciągu',
    name_de: 'Kabel-Seitheben',
    primary_muscle: 'shoulders',
    secondary_muscles: [],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'push',
  },
  {
    id: 'rear_delt_fly',
    name: 'Rear Delt Fly',
    name_pl: 'Odwrotne rozpiętki',
    name_de: 'Reverse Fly',
    primary_muscle: 'shoulders',
    secondary_muscles: ['back'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'pull',
  },

  // ===========================================================================
  // PULL pattern
  // ===========================================================================
  {
    id: 'pullup',
    name: 'Pull-up',
    name_pl: 'Podciąganie nachwytem',
    name_de: 'Klimmzug',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['bodyweight'],
    difficulty: 'advanced',
    contraindications: ['elbow_issue'],
    movement_pattern: 'pull',
  },
  {
    id: 'chinup',
    name: 'Chin-up',
    name_pl: 'Podciąganie podchwytem',
    name_de: 'Chin-up',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['bodyweight'],
    difficulty: 'advanced',
    contraindications: ['elbow_issue'],
    movement_pattern: 'pull',
  },
  {
    id: 'row_bb',
    name: 'Barbell Row',
    name_pl: 'Wiosłowanie sztangą',
    name_de: 'Langhantel-Rudern',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'pull',
  },
  {
    id: 'row_db',
    name: 'Dumbbell Row',
    name_pl: 'Wiosłowanie hantlą',
    name_de: 'Kurzhantel-Rudern',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'pull',
  },
  {
    id: 'cable_row',
    name: 'Cable Seated Row',
    name_pl: 'Wiosłowanie na wyciągu siedząc',
    name_de: 'Kabelrudern sitzend',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'pull',
  },
  {
    id: 't_bar_row',
    name: 'T-Bar Row',
    name_pl: 'Wiosłowanie sztangą jednorącz (T-bar)',
    name_de: 'T-Bar-Rudern',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'pull',
  },
  {
    id: 'lat_pulldown',
    name: 'Lat Pulldown',
    name_pl: 'Ściąganie drążka wyciągu',
    name_de: 'Latzug',
    primary_muscle: 'back',
    secondary_muscles: ['biceps'],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'pull',
  },
  {
    id: 'face_pull',
    name: 'Face Pull',
    name_pl: 'Przyciąganie liny do twarzy',
    name_de: 'Face Pull',
    primary_muscle: 'shoulders',
    secondary_muscles: ['back'],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'pull',
  },

  // ===========================================================================
  // Arms
  // ===========================================================================
  {
    id: 'db_curl',
    name: 'Dumbbell Curl',
    name_pl: 'Uginanie hantli',
    name_de: 'Bizeps-Curl mit Kurzhantel',
    primary_muscle: 'biceps',
    secondary_muscles: [],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['elbow_issue'],
    movement_pattern: 'pull',
  },
  {
    id: 'hammer_curl',
    name: 'Hammer Curl',
    name_pl: 'Uginanie hantli "młotek"',
    name_de: 'Hammer-Curl',
    primary_muscle: 'biceps',
    secondary_muscles: [],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['elbow_issue'],
    movement_pattern: 'pull',
  },
  {
    id: 'preacher_curl',
    name: 'Preacher Curl',
    name_pl: 'Uginanie na modlitewniku',
    name_de: 'Scott-Curl',
    primary_muscle: 'biceps',
    secondary_muscles: [],
    equipment_required: ['machines'],
    difficulty: 'beginner',
    contraindications: ['elbow_issue'],
    movement_pattern: 'pull',
  },
  {
    id: 'cable_curl',
    name: 'Cable Curl',
    name_pl: 'Uginanie na wyciągu',
    name_de: 'Kabel-Curl',
    primary_muscle: 'biceps',
    secondary_muscles: [],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: ['elbow_issue'],
    movement_pattern: 'pull',
  },
  {
    id: 'triceps_pushdown',
    name: 'Triceps Pushdown',
    name_pl: 'Prostowanie linki wyciągu',
    name_de: 'Trizepsdrücken am Kabel',
    primary_muscle: 'triceps',
    secondary_muscles: [],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: ['elbow_issue'],
    movement_pattern: 'push',
  },
  {
    id: 'overhead_triceps_ext',
    name: 'Overhead Triceps Extension',
    name_pl: 'Prostowanie ramion nad głową',
    name_de: 'Trizepsdrücken über Kopf',
    primary_muscle: 'triceps',
    secondary_muscles: [],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement', 'elbow_issue'],
    movement_pattern: 'push',
  },
  {
    id: 'close_grip_bench',
    name: 'Close-grip Bench Press',
    name_pl: 'Wyciskanie sztangi wąskim chwytem',
    name_de: 'Enges Bankdrücken',
    primary_muscle: 'triceps',
    secondary_muscles: ['chest', 'shoulders'],
    equipment_required: ['barbell'],
    difficulty: 'intermediate',
    contraindications: ['elbow_issue', 'wrist_issue'],
    movement_pattern: 'push',
  },

  // ===========================================================================
  // Core
  // ===========================================================================
  {
    id: 'plank',
    name: 'Plank',
    name_pl: 'Deska (plank)',
    name_de: 'Plank (Unterarmstütz)',
    primary_muscle: 'core',
    secondary_muscles: ['shoulders'],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'core',
  },
  {
    id: 'side_plank',
    name: 'Side Plank',
    name_pl: 'Deska bokiem',
    name_de: 'Seitstütz',
    primary_muscle: 'core',
    secondary_muscles: ['shoulders'],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: ['shoulder_impingement'],
    movement_pattern: 'core',
  },
  {
    id: 'dead_bug',
    name: 'Dead Bug',
    name_pl: 'Dead bug',
    name_de: 'Dead Bug',
    primary_muscle: 'core',
    secondary_muscles: [],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'core',
  },
  {
    id: 'hanging_leg_raise',
    name: 'Hanging Leg Raise',
    name_pl: 'Unoszenie nóg w zwisie',
    name_de: 'Hängendes Beinheben',
    primary_muscle: 'core',
    secondary_muscles: [],
    equipment_required: ['bodyweight'],
    difficulty: 'advanced',
    contraindications: [],
    movement_pattern: 'core',
  },
  {
    id: 'ab_wheel',
    name: 'Ab Wheel Rollout',
    name_pl: 'Rollout kółkiem',
    name_de: 'Bauchroller',
    primary_muscle: 'core',
    secondary_muscles: ['shoulders'],
    equipment_required: ['bodyweight'],
    difficulty: 'advanced',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'core',
  },
  {
    id: 'pallof_press',
    name: 'Pallof Press',
    name_pl: 'Pallof press',
    name_de: 'Pallof Press',
    primary_muscle: 'core',
    secondary_muscles: ['shoulders'],
    equipment_required: ['cables'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'core',
  },
  {
    id: 'cable_woodchop',
    name: 'Cable Woodchop',
    name_pl: 'Drwal na wyciągu',
    name_de: 'Holzfäller am Kabel',
    primary_muscle: 'core',
    secondary_muscles: ['shoulders'],
    equipment_required: ['cables'],
    difficulty: 'intermediate',
    contraindications: ['lower_back_injury'],
    movement_pattern: 'core',
  },

  // ===========================================================================
  // Carry / calves
  // ===========================================================================
  {
    id: 'farmer_carry',
    name: 'Farmer Carry',
    name_pl: 'Marsz farmera',
    name_de: "Farmer's Walk",
    primary_muscle: 'core',
    secondary_muscles: ['back'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'carry',
  },
  {
    id: 'suitcase_carry',
    name: 'Suitcase Carry',
    name_pl: 'Marsz z hantlą jednorącz',
    name_de: 'Suitcase Carry',
    primary_muscle: 'core',
    secondary_muscles: ['back'],
    equipment_required: ['dumbbells'],
    difficulty: 'beginner',
    contraindications: [],
    movement_pattern: 'carry',
  },
  {
    id: 'calf_raise',
    name: 'Calf Raise',
    name_pl: 'Wspięcia na palce',
    name_de: 'Wadenheben',
    primary_muscle: 'calves',
    secondary_muscles: [],
    equipment_required: ['bodyweight'],
    difficulty: 'beginner',
    contraindications: ['ankle_issue'],
    movement_pattern: 'push',
  },
  {
    id: 'seated_calf_raise',
    name: 'Seated Calf Raise',
    name_pl: 'Wspięcia siedząc',
    name_de: 'Wadenheben sitzend',
    primary_muscle: 'calves',
    secondary_muscles: [],
    equipment_required: ['machines'],
    difficulty: 'beginner',
    contraindications: ['ankle_issue'],
    movement_pattern: 'push',
  },
];

export function isCompound(ex: Exercise): boolean {
  return ex.secondary_muscles.length >= 2;
}

const BY_ID = new Map(FIXTURE_EXERCISES.map((e) => [e.id, e]));

/** Look up a fixture exercise by id. Returns undefined when unknown. */
export function findFixtureExercise(id: string): Exercise | undefined {
  return BY_ID.get(id);
}
